/**
 * Agent store — leaf module (Perf 3.1.b Step 5). Cross-store refs via runtime getStores().
 */
import { create } from 'zustand';
import { agentsApi } from '@/lib/api/agents';

const getStores = () => require('@/store');
// Only the latest search may update results, including after reset/logout.
let searchRequestId = 0;

const EMPTY_MY_AGENTS_PAGINATION = {
  current: 1,
  page: 1,
  pages: 1,
  perPage: 50,
  total: 0,
  hasMore: false,
};

const useAgentStore = create((set, get) => ({
  // Data states
  agents: {}, // Map of all agents by id
  categories: {}, // Map of categories by id
  categoriesWithAgentIds: [], // Array of category ids with their agent ids
  categoryAgentIds: [], // Array of agent ids for current category
  currentCategoryId: null,
  searchResultIds: [], // Array of agent ids from search
  myAgentIds: [], // Array of agent ids for my agents
  recentAgentIds: [], // Array of recently used agent ids
  featuredAgentIds: [], // Array of featured official agent ids
  topAgentsByCategory: {}, // Map of category id to array of top agent ids

  // Loading states
  isLoadingAgentCategories: false,
  isLoadingCategory: false,
  isLoadingAgentSearch: false,
  isLoadingMyAgents: false,
  isLoadingRecentAgents: false,
  isLoadingFeaturedAgents: false,
  isUpdatingAgent: false,

  // Fetch completion states
  isFetchedCategories: false,
  isFetchedRecentAgents: false,
  isFetchedFeaturedAgents: false,

  // Error states
  categoriesError: null,
  categoryError: null,
  searchError: null,
  myAgentsError: null,
  recentAgentsError: null,
  featuredAgentsError: null,
  updateError: null,

  // Negative cache of agent IDs that returned 404 from /json/agents/:id.
  // Prevents a single disabled agent from generating one 404 per session
  // per user per stale reference (bookmarks, persisted chats with agentId,
  // browser history). The agent store is not persisted, so the cache
  // naturally resets every session — if the agent is later re-enabled,
  // a fresh tab will re-fetch it. See `fetchAgentById`.
  //
  // Important: this only short-circuits the explicit detail fetch. Past
  // conversations still display their messages regardless of whether the
  // agent object is in the store (chat rendering uses `getAgentById`,
  // which is a pure cache lookup and never throws).
  _disabledAgentIds: new Set(),

  // Pagination states
  pagination: {
    categories: { current: 1, total: 1 },
    category: { current: 1, total: 1 },
    landingCategories: {},
    search: { current: 1, total: 1 },
    myAgents: { ...EMPTY_MY_AGENTS_PAGINATION }
  },

  // Add loading and error states for single agent
  isLoadingAgent: false,
  agentError: null,

  // Add loading and error states for delete operation
  isDeletingAgent: false,
  deleteError: null,

  // Helper actions
  addAgents: (newAgents) => {
    if (!Array.isArray(newAgents)) {
      newAgents = [newAgents];
    }

    // Deduplicate agents by ID using a Map
    const uniqueAgentsMap = new Map();
    newAgents.forEach(agent => {
      if (agent && agent.id) {
        uniqueAgentsMap.set(agent.id, agent);
      }
    });
    newAgents = Array.from(uniqueAgentsMap.values());
    
    const processedAgents = newAgents.map(agent => {
      return {
        id: agent.id,
        avatarPath: agent.avatarPath,
        category: agent.category, // : todo :
        defaultPrompts: agent.defaultPrompts,
        jobRoles: agent.jobRoles,
        description: agent.description,
        flags: {
          isEnabled: agent?.flags?.isEnabled,
          isFeatured: agent?.flags?.isFeatured,
          isPlusOnly: agent?.flags?.isPlusOnly,
          isFeaturedOnCharactersPage: agent?.flags?.isFeaturedOnCharactersPage,
          isFeaturedOnCharactersPageTop: agent?.flags?.isFeaturedOnCharactersPageTop,
          isImageInvertable: agent?.flags?.isImageInvertable,
        },
        isOwner: agent.isOwner,
        system: agent.system,
        defaultModelId: agent.defaultModel || null,
        defaultWorkModelId: agent.defaultWorkModel || null,
        createdAt: agent.createdAt,
        inputTypes: agent.inputTypes,
        outputTypes: agent.outputTypes,
        name: agent.name,
        slug: agent.slug,
        stats: agent.stats,
        tags: agent.tags,
        updatedAt: agent.updatedAt,
        visibility: agent.visibility,
        meta: agent.meta,
        bio: agent.bio,
      };
    });
    
    set(state => ({
      agents: {
        ...state.agents,
        ...Object.fromEntries(processedAgents.map(agent => [agent.id, agent]))
      }
    }));

    return processedAgents;
  },

  addAgentCategories: (newCategories) => {
    if (!Array.isArray(newCategories)) {
      newCategories = [newCategories];
    }
    
    set(state => ({
      categories: {
        ...state.categories,
        ...Object.fromEntries(newCategories.map(category => [category.slug, category]))
      }
    }));
  },

  getAgentById: (id) => {
    return get().agents[id];
  },

  getAgentCategoryById: (id) => {
    return get().categories[id];
  },

  fetchAgentById: async (agentId) => {
    try {
      set({ isLoadingAgent: true, agentError: null });
      
      // Negative cache: this ID previously 404'd this session. Don't re-ask.
      // Caps repeat-404 volume at one per session per disabled agent per
      // user, regardless of how many stale references (bookmarks, persisted
      // chats, history entries) they click.
      if (get()._disabledAgentIds.has(agentId)) {
        set({ isLoadingAgent: false });
        return null;
      }

      // Check if we already have this agent in the store
      const existingAgent = get().getAgentById(agentId);
      if (existingAgent) {
        set({ isLoadingAgent: false });
        return existingAgent;
      }

      try {
        const agent = await agentsApi.getAgentById(agentId);
        // Use addAgents helper to update the store
        get().addAgents([agent]);
        set({ isLoadingAgent: false });
        return agent;
      } catch (innerError) {
        // Only cache definitive "not found" responses. Network errors,
        // 5xx, auth failures etc. are transient and must remain retryable.
        const status = innerError?.status ?? innerError?.statusCode;
        const isNotFound = status === 404
          || (typeof innerError?.message === 'string' && /not found/i.test(innerError.message));
        if (isNotFound) {
          get()._disabledAgentIds.add(agentId);
        }
        throw innerError;
      }
    } catch (error) {
      set({ 
        agentError: error.message || 'Failed to fetch agent',
        isLoadingAgent: false 
      });
      throw error;
    }
  },

  fetchAgentCategories: async (page = 1) => {
    // Skip if already fetched or currently loading
    if (get().isFetchedCategories || get().isLoadingAgentCategories) {
      return;
    }

    try {
      set({ isLoadingAgentCategories: true, categoriesError: null });
      
      // First fetch the basic category list
      const basicData = await agentsApi.getAllCategories();
      const categories = basicData.categories.map(category => ({
        id: category.slug,
        name: category.name,
        description: category.description,
        slug: category.slug,
        stats: category.stats,
        order: category.order,
        examples: category.examples,
        meta: category.meta,
      }));
      get().addAgentCategories(categories);
      
      // Then fetch categories with their top agents
      // The landing catalog is the category navigation; include every section.
      const data = await agentsApi.getCategories(page, Math.max(10, categories.length));
      
      // Add all agents to the agents map
      const allAgents = data.categories.flatMap(category => category.topAgents || []);
      get().addAgents(allAgents);
      
      // Store the relationships and top agents
      const topAgentsByCategory = {};
      data.categories.forEach(category => {
        topAgentsByCategory[category.slug] = (category.topAgents || []).map(agent => agent.id);
      });
      
      set(state => ({ 
        categoriesWithAgentIds: data.categories.map(category => ({
          id: category.slug,
          agentIds: (category.topAgents || []).map(agent => agent.id)
        })),
        topAgentsByCategory,
        isFetchedCategories: true,
        pagination: {
          ...state.pagination,
          categories: { 
            current: data.pagination.current,
            total: data.pagination.pages 
          },
          landingCategories: {},
        }
      }));
    } catch (error) {
      set({ categoriesError: error.message });
    } finally {
      set({
        isLoadingAgentCategories: false,
        isFetchedCategories: true,
      });
    }
  },

  fetchCategoryAgents: async (slug, page = 1) => {
    try {
      set({ isLoadingCategory: true, categoryError: null });
      const data = await agentsApi.getByCategory(slug, page);
      
      // Add agents to the agents map
      get().addAgents(data.agents);

      // Add category to categories map if it exists
      if (data.category) {
        get().addAgentCategories(data.category);
      }
      
      set(state => ({ 
        categoryAgentIds: (() => {
          const incomingIds = data.agents.map(agent => agent.id);
          const merged = page > 1 
            ? [...state.categoryAgentIds, ...incomingIds]
            : incomingIds;
          return Array.from(new Set(merged));
        })(),
        currentCategoryId: data.category?.id || null,
        pagination: {
          ...state.pagination,
          category: {
            current: data.pagination.current,
            hasMore: data.pagination.hasMore,
            page: data.pagination.page || data.pagination.current,
            pages: data.pagination.pages,
            perPage: data.pagination.perPage,
            total: data.pagination.total,
          }
        }
      }));
    } catch (error) {
      set({ categoryError: error.message });
    } finally {
      set({ isLoadingCategory: false });
    }
  },

  // Append the next page of a category onto the landing directory without
  // touching the filtered-category buffer used by /agents?category=…
  loadMoreLandingCategoryAgents: async (slug) => {
    if (!slug) return;
    const existingPage = get().pagination.landingCategories?.[slug];
    if (existingPage && existingPage.hasMore === false) return;
    const nextPage = existingPage?.page ? existingPage.page + 1 : 1;
    const data = await agentsApi.getByCategory(slug, nextPage);
    get().addAgents(data.agents);
    if (data.category) get().addAgentCategories(data.category);
    const incomingIds = [];
    for (const agent of data.agents || []) {
      if (agent?.id) incomingIds.push(agent.id);
    }
    set(state => {
      const found = (state.categoriesWithAgentIds || []).some(cat => cat.id === slug);
      const categoriesWithAgentIds = found
        ? state.categoriesWithAgentIds.map(cat => {
          if (cat.id !== slug) return cat;
          return { ...cat, agentIds: Array.from(new Set([...(cat.agentIds || []), ...incomingIds])) };
        })
        : [...(state.categoriesWithAgentIds || []), { id: slug, agentIds: incomingIds }];
      return {
        categoriesWithAgentIds,
        pagination: {
          ...state.pagination,
          landingCategories: {
            ...(state.pagination.landingCategories || {}),
            [slug]: {
              page: data.pagination?.page || data.pagination?.current || nextPage,
              hasMore: !!data.pagination?.hasMore,
              total: data.pagination?.total,
            },
          },
        },
      };
    });
  },

  searchAgents: async (query, page = 1) => {
    const requestId = ++searchRequestId;
    // Clamp to the backend limit: /json/agents/search rejects q > 100 chars.
    // Apply here so every entry point (search page, AgentSelector,
    // AgentSelectorDetailed, @-mentions) is covered uniformly.
    if (typeof query === 'string') query = query.slice(0, 100);
    if (!query.trim()) {
      set({ searchResultIds: [], searchError: null, isLoadingAgentSearch: false });
      return;
    }
    
    try {
      set({ isLoadingAgentSearch: true, searchError: null });
      const data = await agentsApi.search(query, page);
      if (requestId !== searchRequestId) return;
      
      // Add agents to the agents map
      get().addAgents(data.agents);
      
      set(state => ({ 
        searchResultIds: data.agents.map(agent => agent.id),
        pagination: {
          ...state.pagination,
          search: { 
            current: data.pagination.current,
            total: data.pagination.pages 
          }
        }
      }));
    } catch (error) {
      if (requestId === searchRequestId) set({ searchError: error.message });
    } finally {
      if (requestId === searchRequestId) set({ isLoadingAgentSearch: false });
    }
  },

  fetchMyAgents: async (page = 1) => {
    try {
      set({ isLoadingMyAgents: true, myAgentsError: null });
      const data = await agentsApi.getMyAgents(page);

      get().addAgents(data.agents || []);

      set((state) => {
        const incomingIds = [];
        for (const agent of data.agents || []) {
          if (agent?.id) incomingIds.push(agent.id);
        }
        const merged = page > 1
          ? [...state.myAgentIds, ...incomingIds]
          : incomingIds;
        return {
          myAgentIds: Array.from(new Set(merged)),
          pagination: {
            ...state.pagination,
            myAgents: {
              current: data.pagination.current,
              page: data.pagination.page || data.pagination.current,
              pages: data.pagination.pages,
              perPage: data.pagination.perPage,
              total: data.pagination.total,
              hasMore: data.pagination.hasMore,
            },
          },
        };
      });
    } catch (error) {
      set({ myAgentsError: error.message });
    } finally {
      set({ isLoadingMyAgents: false });
    }
  },

  ensureAllMyAgents: async () => {
    const throwIfIncomplete = () => {
      const { myAgentsError, pagination } = get();
      if (myAgentsError) {
        const err = new Error(myAgentsError);
        err.code = 'FETCH_FAILED';
        throw err;
      }
      if (pagination.myAgents?.hasMore) {
        const err = new Error('Could not load every agent');
        err.code = 'INCOMPLETE';
        throw err;
      }
    };

    if (!get().myAgentIds.length) {
      await get().fetchMyAgents(1);
    }
    let guard = 0;
    while (get().pagination.myAgents?.hasMore && guard < 50) {
      if (get().myAgentsError) break;
      const pageBefore = get().pagination.myAgents?.page || 1;
      await get().fetchMyAgents(pageBefore + 1);
      const pageAfter = get().pagination.myAgents?.page || pageBefore;
      if (pageAfter <= pageBefore) break;
      guard += 1;
    }
    throwIfIncomplete();
  },

  addMyAgent: (agent) => {
    if (!agent?.id) return;
    get().addAgents([agent]);
    set((state) => {
      const alreadyListed = state.myAgentIds.includes(agent.id);
      return {
        myAgentIds: alreadyListed ? state.myAgentIds : [agent.id, ...state.myAgentIds],
        pagination: {
          ...state.pagination,
          myAgents: {
            ...state.pagination.myAgents,
            total: (state.pagination.myAgents?.total ?? state.myAgentIds.length) + (alreadyListed ? 0 : 1),
          },
        },
      };
    });
  },

  fetchRecentAgents: async () => {
    // Skip if already fetched or currently loading
    if (get().isFetchedRecentAgents || get().isLoadingRecentAgents) {
      return;
    }

    try {
      set({ isLoadingRecentAgents: true, recentAgentsError: null });
      const agents = await agentsApi.getRecentAgents();
      
      // Add agents to the agents map
      get().addAgents(agents);
      
      set({ 
        recentAgentIds: agents.map(agent => agent.id),
        isLoadingRecentAgents: false,
        isFetchedRecentAgents: true
      });

      return agents;
    } catch (error) {
      set({ 
        recentAgentsError: error.message,
        isLoadingRecentAgents: false, 
        isFetchedRecentAgents: true,
      });
      throw error;
    }
  },

  fetchFeaturedAgents: async () => {
    // Skip if already fetched or currently loading
    if (get().isFetchedFeaturedAgents || get().isLoadingFeaturedAgents) {
      return;
    }

    try {
      // : todo :
      // this calls like a million times      
      set({ isLoadingFeaturedAgents: true, featuredAgentsError: null });
      const agents = []; // await agentsApi.getFeaturedOfficialAgents();
      
      // Add agents to the agents map
      get().addAgents(agents);
      
      set({ 
        featuredAgentIds: agents.map(agent => agent.id),
        isLoadingFeaturedAgents: false,
        isFetchedFeaturedAgents: true
      });

      return agents;
    } catch (error) {
      set({ 
        featuredAgentsError: error.message,
        isLoadingFeaturedAgents: false,
        isFetchedFeaturedAgents: true,
      });
      throw error;
    }
  },

  resetCategory: () => {
    set({ 
      categoryAgentIds: [],
      currentCategoryId: null,
      categoryError: null,
      pagination: {
        ...get().pagination,
        category: { current: 1, total: 1 }
      }
    });
  },

  resetSearch: () => {
    searchRequestId += 1;
    set({ 
      searchResultIds: [],
      searchError: null,
      isLoadingAgentSearch: false,
      pagination: {
        ...get().pagination,
        search: { current: 1, total: 1 }
      }
    });
  },

  // Clear all states
  clearAll: () => {
    searchRequestId += 1;
    set({
      agents: {},
      categories: {},
      categoriesWithAgentIds: [],
      categoryAgentIds: [],
      currentCategoryId: null,
      searchResultIds: [],
      myAgentIds: [],
      recentAgentIds: [],
      featuredAgentIds: [],
      topAgentsByCategory: {},
      isLoadingAgentCategories: false,
      isLoadingCategory: false,
      isLoadingAgentSearch: false,
      isLoadingMyAgents: false,
      isLoadingRecentAgents: false,
      isLoadingFeaturedAgents: false,
      isFetchedCategories: false,
      isFetchedRecentAgents: false,
      isFetchedFeaturedAgents: false,
      categoriesError: null,
      categoryError: null,
      searchError: null,
      myAgentsError: null,
      recentAgentsError: null,
      featuredAgentsError: null,
      updateError: null,
      pagination: {
        categories: { current: 1, total: 1 },
        category: { current: 1, total: 1 },
        landingCategories: {},
        search: { current: 1, total: 1 },
        myAgents: { ...EMPTY_MY_AGENTS_PAGINATION }
      }
    });
  },

  updateAgent: async (agentId, formData) => {
    try {
      set({ isUpdatingAgent: true, updateError: null });
      
      const response = await agentsApi.update(agentId, formData);
      
      if (response.error) {
        throw new Error(response.message || 'Failed to update agent');
      }

      // Update the agent in the store
      set(state => ({
        agents: {
          ...state.agents,
          [agentId]: response.agent
        },
        isUpdatingAgent: false
      }));

      return response.agent;
    } catch (error) {
      set({ 
        updateError: error.message || 'Failed to update agent',
        isUpdatingAgent: false 
      });
      throw error;
    }
  },

  deleteAgent: async (agentId) => {
    try {
      set({ isDeletingAgent: true, deleteError: null });
      
      const response = await agentsApi.deleteAgent(agentId);
      
      if (response.error) {
        throw new Error(response.message || 'Failed to delete agent');
      }

      // Remove the agent from the store
      set(state => {
        const { [agentId]: deletedAgent, ...remainingAgents } = state.agents;
        return {
          agents: remainingAgents,
          isDeletingAgent: false,
          // Remove from other arrays if present
          myAgentIds: state.myAgentIds.filter(id => id !== agentId),
          searchResultIds: state.searchResultIds.filter(id => id !== agentId),
          categoryAgentIds: state.categoryAgentIds.filter(id => id !== agentId)
        };
      });

      // Also remove from user's favorite agents if present
      const currentUser = getStores().useAuthStore.getState().user;
      if (currentUser.favoriteAgentIds?.includes(agentId)) {
        getStores().useAuthStore.getState().removeFavoriteAgentId(agentId);
      }

      return response;
    } catch (error) {
      set({ 
        deleteError: error.message || 'Failed to delete agent',
        isDeletingAgent: false 
      });
      throw error;
    }
  },

  clearAgents: () => {
    set({
      agents: {},
      categories: {},
      categoriesWithAgentIds: [],
      categoryAgentIds: [],
      currentCategoryId: null,
      searchResultIds: [],
      myAgentIds: [],
      recentAgentIds: [],
      featuredAgentIds: [],
      topAgentsByCategory: {},
      isLoadingAgentCategories: false,
      isLoadingCategory: false,
      isLoadingAgentSearch: false,
      isLoadingMyAgents: false,
      isLoadingRecentAgents: false,
      isLoadingFeaturedAgents: false,
      isFetchedCategories: false,
      isFetchedRecentAgents: false,
      isFetchedFeaturedAgents: false,
      categoriesError: null,
      categoryError: null,
      searchError: null,
      myAgentsError: null,
      recentAgentsError: null,
      featuredAgentsError: null,
      updateError: null,
      pagination: {
        categories: { current: 1, total: 1 },
        category: { current: 1, total: 1 },
        landingCategories: {},
        search: { current: 1, total: 1 },
        myAgents: { ...EMPTY_MY_AGENTS_PAGINATION }
      }
    });
  },
}));

export { useAgentStore };
export default useAgentStore;
